// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useParams } from "react-router-dom";
import { ColumnsType } from "antd/es/table";
import clsx from "clsx";
import ReactCountryFlag from "react-country-flag";
import moment from "moment";

// Local Imports
import CustomModal from "../../components/custom-modal/custom-modal";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import TakeSubscriptionActionService from "./take-subscription-action.service";
import CollapseComponent from "../../components/collapse.component";
import TakePortfolioActionService from "../take-portfolio-action/take-portfolio-action.service";
import TakeAction from "../../components/take-action/take-action";
import TakeDepositActionService from "../take-deposit-action/take-deposit-action.service";
import { extractToEntity, formatNumber } from "../../utils/helper/functions";
import CustomTable from "../../components/custom-table/custom-table";
import { Divider, Image } from "antd";
import PdfViewer from "src/components/pdf-viewer";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const TakeSubscriptionAction: React.FC = (): ReactElement => {
  const user = useSelector((state: any) => state?.user);
  const takeSubscriptionActionService = new TakeSubscriptionActionService();
  const takePortfolioActionService = new TakePortfolioActionService();
  const takeDepositActionService = new TakeDepositActionService();
  const { subscriptionId }: { [key: string]: string | undefined } = useParams();
  const [subscriptionDetailsData, setSubscriptionDetailsData] = useState<any>();
  const [openRejectPortfolioModal, setOpenRejectPortfolioModal] =
    useState<boolean>(false);
  const [openRejectDepositModal, setOpenRejectDepositModal] =
    useState<boolean>(false);
  const [openApproveDepositModal, setOpenApproveDepositModal] =
    useState<boolean>(false);
  const [openRejectSubscriptionModal, setOpenRejectSubscriptionModal] =
    useState<boolean>(false);
  const [openApproveSubscriptionModal, setOpenApproveSubscriptionModal] =
    useState<boolean>(false);
  const [openApprovePortfolioModal, setOpenApprovePortfolioModal] =
    useState<boolean>(false);
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});

  useEffect(() => {
    fetchPortfolioDetailsData();
  }, []);

  const onRejectPortfolio = async (value: string) => {
    const response: string | undefined =
      await takePortfolioActionService.rejectPortfolio(
        subscriptionDetailsData?.portfolio?.id,
        value
      );
    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenRejectPortfolioModal(false);
    }
  };

  const onApprovePortfolio = async (date: string) => {
    const formatedDate = dayjs(date)
      .hour(12)
      .minute(0)
      .second(0)
      .millisecond(0);
    const response: string | undefined =
      await takePortfolioActionService.ApprovePortfolio(
        subscriptionDetailsData?.portfolio?.id,
        formatedDate
      );
    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenApprovePortfolioModal(false);
    }
  };

  const onApproveDeposit = async (value: string) => {
    const response: string | undefined =
      await takeDepositActionService.takeAction(
        subscriptionDetailsData?.deposit?.id,
        "approve",
        value
      );
    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenApproveDepositModal(false);
    }
  };

  const onRejectDeposit = async (value: string) => {
    const response: string | undefined =
      await takeDepositActionService.takeAction(
        subscriptionDetailsData?.deposit?.id,
        "reject",
        value
      );
    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenRejectDepositModal(false);
    }
  };

  const onApproveSubscription = async (value: string) => {
    const response: string | undefined =
      await takeSubscriptionActionService.takeAction(
        subscriptionId,
        "approve",
        value
      );

    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenApproveSubscriptionModal(false);
    }
  };

  const onRejectSubscription = async (value: string) => {
    const response: string | undefined =
      await takeSubscriptionActionService.takeAction(
        subscriptionId,
        "reject",
        value
      );

    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenRejectSubscriptionModal(false);
    }
  };

  const fetchPortfolioDetailsData = async (): Promise<void> => {
    const data = await takeSubscriptionActionService.getSubscriptionDetailsData(
      subscriptionId
    );

    if (data !== undefined) {
      setSubscriptionDetailsData(data);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      fixed: "left",
      render: (_: any, record: any) => (
        <span>{formatNumber(record?.convertedAmount)}</span>
      ),
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      fixed: "left",
      render: (_: any, record: any) => <span>{record?.originalCurrency}</span>,
    },
  ];

  return (
    subscriptionDetailsData && (
      <section className="flex flex-col">
        <BreadcrumbComponent />
        <div className="flex flex-col pb-7 flex-grow">
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">Portfolio details </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account#
                </p>
                <Link
                  to={`/home/business-account/${subscriptionDetailsData?.accountId}/${subscriptionDetailsData?.portfolio?.account?.activeKycId}/details`}
                  className=" text-base font-semibold text-lightGray-200 underline"
                >
                  {subscriptionDetailsData?.portfolio?.account?.accountCode}
                </Link>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {
                    subscriptionDetailsData?.portfolio?.account?.clientType
                      ?.description
                  }{" "}
                  -{" "}
                  {
                    subscriptionDetailsData?.portfolio?.account?.clientSubType
                      ?.description
                  }
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account status
                </p>
                <span className=" text-base font-semibold w-fit px-3 py-1 rounded-md bg-green-100 text-white">
                  {
                    subscriptionDetailsData?.portfolio?.account?.accountStatus
                      ?.description
                  }
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio#
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.status !== "Rejected"
                    ? subscriptionDetailsData?.portfolio?.accountCode ||
                      subscriptionDetailsData?.portfolio?.account?.accountCode +
                        " - New"
                    : "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.managementType}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio status
                </p>
                <span
                  className={clsx(
                    " text-base font-semibold w-fit px-3 py-1 rounded-md  text-white",
                    subscriptionDetailsData?.portfolio?.status === "Opened"
                      ? "bg-green-100"
                      : subscriptionDetailsData?.portfolio?.status ===
                        "Rejected"
                      ? "bg-red-200"
                      : "bg-yellow-500"
                  )}
                >
                  {subscriptionDetailsData?.portfolio?.status === "Pending"
                    ? "New"
                    : subscriptionDetailsData?.portfolio?.status ===
                      "PENDING_DEPOSIT"
                    ? "Pending Deposit"
                    : subscriptionDetailsData?.portfolio?.status}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.portfolioCurrency || "-"}
                </span>
              </div>
              {subscriptionDetailsData?.portfolio?.openDate && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Open date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(
                      subscriptionDetailsData?.portfolio?.openDate
                    ).format("DD MMM, YYYY")}
                  </span>
                </div>
              )}
              {subscriptionDetailsData?.portfolio?.status === "Rejected" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Status reason
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.portfolio
                      ?.portfolioStatusReason || "-"}
                  </span>
                </div>
              )}
              {subscriptionDetailsData?.portfolio?.status !== "Pending" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Action by
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.portfolio?.actionBy?.name || "-"}
                  </span>
                </div>
              )}
              {subscriptionDetailsData?.portfolio?.status !== "Pending" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Action date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.portfolio?.actionDate
                      ? moment(
                          subscriptionDetailsData?.portfolio?.actionDate
                        ).format("DD MMM, YYYY hh:mm A")
                      : "-"}
                  </span>
                </div>
              )}

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.account?.wealthManager
                    ?.ae1 || "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.account?.wealthManager
                    ?.ae1Email || "-"}
                </span>
              </div>
              {subscriptionDetailsData?.portfolio?.account?.wealthManager
                ?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2 || "-"}
                  </span>
                </div>
              )}
              {subscriptionDetailsData?.portfolio?.account?.wealthManager
                ?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager email
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2Email || "-"}
                  </span>
                </div>
              )}

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Online trading
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.portfolio?.onlineTradeSys
                    ? "Yes"
                    : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Pledged
                </p>
                <span
                  className={clsx(
                    "rounded-md w-fit",
                    subscriptionDetailsData?.portfolio?.portfolioDisclaimer
                      ? "bg-[#FFAF00] text-white text-sm font-medium p-2"
                      : "bg-transparent text-lightGray-200 font-semibold text-base p-0"
                  )}
                >
                  {subscriptionDetailsData?.portfolio?.portfolioDisclaimer
                    ? extractToEntity(
                        subscriptionDetailsData?.portfolio?.portfolioDisclaimer
                      )
                    : "No"}
                </span>
              </div>
            </div>
            {subscriptionDetailsData?.portfolio?.agreementPdf ? (
              <div className="view flex align-center">
                <img
                  src={"/images/info.svg"}
                  alt="view info portfolio agreement"
                />

                <span
                  className="underline cursor-pointer text-blue-500"
                  onClick={() => {
                    setSelectedDocument(
                      subscriptionDetailsData?.portfolio?.agreementPdf
                    );
                    setOpenPdfViewerModal(true);
                  }}
                >
                  View portfolio agreement
                </span>
              </div>
            ) : (
              ""
            )}
            {subscriptionDetailsData?.portfolio?.status === "Pending" &&
              user?.permissions?.includes("request_portfolios_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectPortfolioModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApprovePortfolioModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    {subscriptionDetailsData?.depositId
                      ? "Step 1/3 - Create portfolio"
                      : "Step 1/2 - Create portfolio"}
                  </button>
                </div>
              )}
          </CollapseComponent>

          {subscriptionDetailsData?.deposit && (
            <CollapseComponent
              header={
                <div className="flex gap-3 items-center">
                  <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                  <h3 className=" text-xl font-semibold">
                    Deposit details{" "}
                    {subscriptionDetailsData?.deposit?.status === "Approved" ||
                    subscriptionDetailsData?.deposit?.status === "Rejected" ||
                    subscriptionDetailsData?.portfolio?.status ===
                      "Rejected" ? (
                      <span
                        className={clsx(
                          "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                          subscriptionDetailsData?.deposit?.status ===
                            "Approved"
                            ? "bg-green-100"
                            : "bg-red-200"
                        )}
                      >
                        {subscriptionDetailsData?.portfolio?.status ===
                        "Rejected"
                          ? "Cancelled"
                          : subscriptionDetailsData?.deposit?.status}
                      </span>
                    ) : (
                      ""
                    )}
                  </h3>
                </div>
              }
            >
              <div className="flex gap-5 flex-wrap mb-5">
                <div className="w-[32%] flex flex-col">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Amount
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {formatNumber(subscriptionDetailsData?.deposit?.amount)}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Currency
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.deposit?.currency}
                  </span>
                </div>
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Payment method
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.deposit?.paymentMethod}
                  </span>
                </div>

                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Payment ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.gatewayPaymentId}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Reference ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.gatewayRef}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Result
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.gatewayResult}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Tracking ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.gatewayTrackId}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Transaction ID
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.gatewayTransId}
                    </span>
                  </div>
                )}

                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" &&
                  subscriptionDetailsData?.deposit?.card?.cardNumber && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Card number
                      </p>
                      <div className="flex items-center gap-2">
                        <span className=" text-base font-semibold text-lightGray-200">
                          {subscriptionDetailsData?.deposit?.card?.cardNumber}
                        </span>
                        {!subscriptionDetailsData?.deposit?.card?.cardImage && (
                          <img src={`/images/tick-circle.svg`} alt="" />
                        )}
                      </div>
                    </div>
                  )}

                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" &&
                  subscriptionDetailsData?.deposit?.card?.cardHolderName && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Name on card
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.card?.cardHolderName}
                      </span>
                    </div>
                  )}
                {subscriptionDetailsData?.deposit?.paymentMethod === "Knet" &&
                  subscriptionDetailsData?.deposit?.card?.cardExpiryDate && (
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Expiry date
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.card?.cardExpiryDate}
                      </span>
                    </div>
                  )}

                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && <Divider />}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <h3 className="text-lg w-full font-semibold">Kamco bank</h3>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Bank name
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.kamcoBank?.accountName}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Country
                    </p>
                    <div className="flex items-center gap-2">
                      <ReactCountryFlag
                        style={{
                          fontSize: "24px",
                        }}
                        countryCode={
                          subscriptionDetailsData?.deposit?.kamcoBank?.country
                            ?.countryCode
                        }
                        svg
                      />
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.kamcoBank?.country
                          ?.nameEn || "-"}
                      </span>
                    </div>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      IBAN
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.kamcoBank?.iban}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Swift code
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.kamcoBank?.swiftCode}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Bank referenece
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.kamcoBank?.refrence}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && <Divider />}

                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <h3 className="text-lg w-full font-semibold">Client bank</h3>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Bank name
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {
                        subscriptionDetailsData?.deposit?.paymentBank?.bank
                          ?.bankName
                      }
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Country
                    </p>
                    <div className="flex items-center gap-2">
                      <ReactCountryFlag
                        style={{
                          fontSize: "24px",
                        }}
                        countryCode={
                          subscriptionDetailsData?.deposit?.paymentBank
                            ?.accountCountry?.countryCode
                        }
                        svg
                      />
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.paymentBank
                          ?.accountCountry?.nameEn || "-"}
                      </span>
                    </div>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      IBAN
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.paymentBank
                        ?.ibanNumber || "-"}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Swift code
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.deposit?.paymentBank
                        ?.swiftCode || "-"}
                    </span>
                  </div>
                )}
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && <Divider />}
                {(subscriptionDetailsData?.deposit?.paymentCardFront ||
                  subscriptionDetailsData?.deposit?.transferReceipt ||
                  subscriptionDetailsData?.deposit?.card?.cardImage) && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Attachment
                    </p>
                    <Image
                      src={
                        subscriptionDetailsData?.deposit?.paymentCardFront ||
                        subscriptionDetailsData?.deposit?.transferReceipt ||
                        subscriptionDetailsData?.deposit?.card?.cardImage
                      }
                      width={"80px"}
                      height={"50px"}
                      alt=""
                    />
                  </div>
                )}
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Client notes
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.deposit?.remarks || "-"}
                  </span>
                </div>
                {subscriptionDetailsData?.deposit?.paymentMethod ===
                  "WireTransfer" && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Request date
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {moment(
                        subscriptionDetailsData?.deposit?.createdAt
                      ).format("DD MMM, YYYY hh:mm A")}
                    </span>
                  </div>
                )}
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Deposit date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.deposit?.depositDate
                      ? moment(
                          subscriptionDetailsData?.deposit?.depositDate
                        ).format("DD MMM, YYYY hh:mm A")
                      : "-"}
                  </span>
                </div>
                {(subscriptionDetailsData?.deposit?.status === "Approved" ||
                  subscriptionDetailsData?.deposit?.status === "Rejected") && (
                  <>
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        {subscriptionDetailsData?.deposit?.status === "Approved"
                          ? "Prospero deposit transaction ID"
                          : "Rejection reason"}
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.status === "Approved"
                          ? subscriptionDetailsData?.deposit?.transactionId
                          : subscriptionDetailsData?.deposit?.statusReason}
                      </span>
                    </div>
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Action by
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {subscriptionDetailsData?.deposit?.actionBy?.name ||
                          "-"}
                      </span>
                    </div>
                    <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                      <p className=" text-black-300 text-xs font-normal mb-[10px]">
                        Action date
                      </p>
                      <span className=" text-base font-semibold text-lightGray-200">
                        {moment(
                          subscriptionDetailsData?.deposit?.actionDate
                        ).format("DD MMM, YYYY hh:mm A") || "-"}
                      </span>
                    </div>
                  </>
                )}
              </div>
              {subscriptionDetailsData?.deposit?.status === "Pending" &&
                subscriptionDetailsData?.portfolio?.status !== "Pending" &&
                subscriptionDetailsData?.portfolio?.status !== "Rejected" &&
                user?.permissions?.includes("request_deposit_approver") && (
                  <div className="flex gap-7 justify-end items-center">
                    <button
                      onClick={() => setOpenRejectDepositModal(true)}
                      className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => setOpenApproveDepositModal(true)}
                      className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                    >
                      {subscriptionDetailsData?.portfolio?.status === "Opened"
                        ? "Step 1/2 - Approve deposit"
                        : "Step 2/3 - Approve deposit"}
                    </button>
                  </div>
                )}
            </CollapseComponent>
          )}
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">
                  Fund details{" "}
                  {subscriptionDetailsData?.status !== "Pending" ||
                  subscriptionDetailsData?.deposit?.status === "Rejected" ||
                  subscriptionDetailsData?.portfolio?.status === "Rejected" ? (
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                        subscriptionDetailsData?.status === "Approved"
                          ? "bg-green-100"
                          : "bg-red-200"
                      )}
                    >
                      {subscriptionDetailsData?.deposit?.status ===
                        "Rejected" ||
                      subscriptionDetailsData?.portfolio?.status === "Rejected"
                        ? "Cancelled"
                        : subscriptionDetailsData?.status}
                    </span>
                  ) : (
                    ""
                  )}
                </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Fund name
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.mutualFund?.NAME}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Subscription amount
                </p>
                <span className=" text-base font-semibold text-lightGray-200 mb-5">
                  {formatNumber(subscriptionDetailsData?.subscriptionAmount) +
                    " " +
                    subscriptionDetailsData?.subscriptionCurrency}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Fees
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {formatNumber(subscriptionDetailsData?.subscriptionFees)}
                </span>
              </div>
              {subscriptionDetailsData?.fromAvailableCash?.length ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <div className="w-full">
                    <CustomTable
                      columns={columns}
                      data={subscriptionDetailsData?.fromAvailableCash}
                      showPagination={false}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {subscriptionDetailsData?.subscriptionCurrency}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Request date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {moment(subscriptionDetailsData?.createdAt).format(
                    "DD MMM, YYYY hh:mm A"
                  )}
                </span>
              </div>
              {subscriptionDetailsData?.status === "Approved" ? (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    PROSPERO transaction number
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {subscriptionDetailsData?.subTransactionId}
                  </span>
                </div>
              ) : (
                ""
              )}
              {(subscriptionDetailsData?.status === "Rejected" ||
                subscriptionDetailsData?.status === "Approved") && (
                <>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action by
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {subscriptionDetailsData?.actionBy?.name || "-"}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action date
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {moment(subscriptionDetailsData?.actionDate).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
            {subscriptionDetailsData?.agreementPdf ? (
              <div className="view flex align-center">
                <img
                  src={"/images/info.svg"}
                  alt="view info portfolio agreement"
                />

                <span
                  className="underline cursor-pointer text-blue-500"
                  onClick={() => {
                    setSelectedDocument(subscriptionDetailsData?.agreementPdf);
                    setOpenPdfViewerModal(true);
                  }}
                >
                  View subscription form
                </span>
              </div>
            ) : (
              ""
            )}
            {subscriptionDetailsData?.deposit?.status !== "Pending" &&
              subscriptionDetailsData?.deposit?.status !== "Rejected" &&
              subscriptionDetailsData?.portfolio?.status !== "Peneding" &&
              subscriptionDetailsData?.portfolio?.status !== "Rejected" &&
              subscriptionDetailsData?.status === "Pending" &&
              user?.permissions?.includes("request_subscription_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectSubscriptionModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApproveSubscriptionModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    {subscriptionDetailsData?.deposit
                      ? "Step 3/3 - Approve subscription"
                      : "Approve subscription"}
                  </button>
                </div>
              )}
          </CollapseComponent>
        </div>

        <CustomModal
          isVisible={openRejectPortfolioModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectPortfolioModal(false);
          }}
        >
          <TakeAction
            label="Rejection reason"
            action="reject"
            fieldName="statusReason"
            errorName="reason"
            onCancel={() => setOpenRejectPortfolioModal(false)}
            onSubmit={(value) => onRejectPortfolio(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openApprovePortfolioModal}
          centered
          width={450}
          onClose={() => {
            setOpenApprovePortfolioModal(false);
          }}
        >
          <TakeAction
            label="Open date"
            action="approve"
            fieldName="openDate"
            errorName="open date"
            date={true}
            onCancel={() => setOpenApprovePortfolioModal(false)}
            onSubmit={(value) => onApprovePortfolio(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openApproveDepositModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveDepositModal(false);
          }}
        >
          <TakeAction
            label="Prospero deposit transaction ID"
            action="approve"
            fieldName="transactionId"
            errorName="deposit transaction ID"
            onCancel={() => setOpenApproveDepositModal(false)}
            onSubmit={(value) => onApproveDeposit(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openRejectDepositModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectDepositModal(false);
          }}
        >
          <TakeAction
            label="Rejection reason"
            action="reject"
            fieldName="statusReason"
            errorName="reason"
            onCancel={() => setOpenRejectDepositModal(false)}
            onSubmit={(value) => onRejectDeposit(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openApproveSubscriptionModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveSubscriptionModal(false);
          }}
        >
          <TakeAction
            label="Prospero subscription transaction ID"
            action="approve"
            fieldName="transactionId"
            errorName="subscription transaction ID"
            onCancel={() => setOpenApproveSubscriptionModal(false)}
            onSubmit={(value) => onApproveSubscription(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openRejectSubscriptionModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectSubscriptionModal(false);
          }}
        >
          <TakeAction
            label="Rejection reason"
            action="reject"
            fieldName="statusReason"
            errorName="reason"
            onCancel={() => setOpenRejectSubscriptionModal(false)}
            onSubmit={(value) => onRejectSubscription(value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openPdfViewerModal}
          centered
          width={1100}
          showClose={false}
          onClose={() => {
            setOpenPdfViewerModal(false);
          }}
          className="edit-modal"
        >
          <div className="flex flex-col items-center justify-center">
            <PdfViewer fileUrl={selectedDocument} />
          </div>
        </CustomModal>
      </section>
    )
  );
};

export default TakeSubscriptionAction;
